import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import MarketingCard from './MarketingCard';

const COPY_TYPES = [
  { id: '30s', label: '30s Ad', icon: '📺' },
  { id: '15s', label: '15s Ad', icon: '⚡' },
  { id: 'social', label: 'Social', icon: '📱' },
  { id: 'email', label: 'Email', icon: '📧' },
  { id: 'website', label: 'Website', icon: '🌐' }
];

const SOCIAL_PLATFORMS = ['Facebook', 'Instagram', 'LinkedIn', 'X', 'TikTok', 'YouTube'];

const MarketingView = ({ board, token, currentUser }) => {
  const [phase, setPhase] = useState('discovery'); // 'discovery' | 'select' | 'tacticChat'
  const [brandHistory, setBrandHistory] = useState([]);
  const [brandContext, setBrandContext] = useState(null);
  const [selectedPlatforms, setSelectedPlatforms] = useState(['Facebook', 'Instagram']);
  const [socialExpanded, setSocialExpanded] = useState(false);
  const [activeTactic, setActiveTactic] = useState(null);
  const [tacticHistories, setTacticHistories] = useState({});
  const [tacticProposals, setTacticProposals] = useState({});
  const [generatedTactics, setGeneratedTactics] = useState(new Set());
  const [cards, setCards] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(true);
  const [chatLoading, setChatLoading] = useState(false);
  const [inputText, setInputText] = useState('');

  const chatEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    fetchCards();
  }, [board._id]); // eslint-disable-line

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [brandHistory, tacticHistories, activeTactic, phase]);

  const fetchCards = useCallback(async () => {
    try {
      setCardsLoading(true);
      const res = await axios.get(`/api/cards/board/${board._id}`, {
        headers: { 'x-auth-token': token }
      });
      setCards(res.data.filter(c => !c.isArchived));
    } catch (err) {
      console.error('Error fetching cards:', err);
    } finally {
      setCardsLoading(false);
    }
  }, [board._id, token]);

  const currentTacticHistory = activeTactic ? (tacticHistories[activeTactic] || []) : [];
  const currentTacticProposal = activeTactic ? (tacticProposals[activeTactic] || null) : null;

  // ── Brand Discovery Phase ──────────────────────────────────────────────────

  const sendBrandMessage = async () => {
    if (!inputText.trim() || chatLoading) return;
    const userMsg = inputText;
    setInputText('');
    setChatLoading(true);

    const newHistory = [...brandHistory, { role: 'user', content: userMsg }];
    setBrandHistory(newHistory);

    try {
      const res = await axios.post('/api/marketing/chat', {
        message: userMsg,
        history: brandHistory,
        boardId: board._id,
        mode: 'brand'
      }, { headers: { 'x-auth-token': token } });

      const data = res.data;
      const assistantMsg = { role: 'assistant', content: data.text };
      setBrandHistory([...newHistory, assistantMsg]);

      if (data.type === 'brand_ready' && data.brandContext) {
        setBrandContext(data.brandContext);
        setTimeout(() => setPhase('select'), 800);
      }
    } catch (err) {
      console.error('Brand chat error:', err);
      setBrandHistory([...newHistory, { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' }]);
    } finally {
      setChatLoading(false);
      inputRef.current?.focus();
    }
  };

  // ── Tactic Select Phase ────────────────────────────────────────────────────

  const handleTacticClick = async (tacticId) => {
    if (tacticId === 'social') {
      setSocialExpanded(prev => !prev);
      return;
    }
    await startTactic(tacticId);
  };

  const handleSocialGenerate = async () => {
    setSocialExpanded(false);
    await startTactic('social');
  };

  const startTactic = async (tacticId) => {
    if (generatedTactics.has(tacticId)) {
      // Already generated — quick generate to append more cards
      await quickGenerate(tacticId);
    } else {
      // New tactic — go to tactic Q&A
      setActiveTactic(tacticId);
      setPhase('tacticChat');
    }
  };

  const quickGenerate = async (tacticId) => {
    setChatLoading(true);
    try {
      const res = await axios.post('/api/marketing/chat', {
        message: 'Generate the copy now.',
        history: [],
        copyType: tacticId,
        boardId: board._id,
        mode: 'quick_generate',
        platforms: tacticId === 'social' ? selectedPlatforms : [],
        brandContext
      }, { headers: { 'x-auth-token': token } });

      const data = res.data;
      if (data.type === 'proposal' && data.proposals?.length > 0) {
        await executeProposals(data.proposals);
        setGeneratedTactics(prev => new Set([...prev, tacticId]));
      }
    } catch (err) {
      console.error('Quick generate error:', err);
      alert('Failed to generate copy: ' + (err.response?.data?.msg || err.message));
    } finally {
      setChatLoading(false);
    }
  };

  // ── Tactic Chat Phase ──────────────────────────────────────────────────────

  const sendTacticMessage = async () => {
    if (!inputText.trim() || chatLoading) return;
    const userMsg = inputText;
    setInputText('');
    setChatLoading(true);

    const newHistory = [...currentTacticHistory, { role: 'user', content: userMsg }];
    setTacticHistories(prev => ({ ...prev, [activeTactic]: newHistory }));

    try {
      const res = await axios.post('/api/marketing/chat', {
        message: userMsg,
        history: currentTacticHistory,
        copyType: activeTactic,
        boardId: board._id,
        mode: 'tactic',
        platforms: activeTactic === 'social' ? selectedPlatforms : [],
        brandContext
      }, { headers: { 'x-auth-token': token } });

      const data = res.data;
      const assistantMsg = { role: 'assistant', content: data.text };
      setTacticHistories(prev => ({ ...prev, [activeTactic]: [...newHistory, assistantMsg] }));

      if (data.type === 'proposal' && data.proposals?.length > 0) {
        setTacticProposals(prev => ({ ...prev, [activeTactic]: data }));
      }
    } catch (err) {
      console.error('Tactic chat error:', err);
      setTacticHistories(prev => ({
        ...prev,
        [activeTactic]: [...newHistory, { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' }]
      }));
    } finally {
      setChatLoading(false);
      inputRef.current?.focus();
    }
  };

  const approveTacticProposal = async () => {
    if (!currentTacticProposal) return;
    await executeProposals(currentTacticProposal.proposals);
    setTacticProposals(prev => ({ ...prev, [activeTactic]: null }));
    setGeneratedTactics(prev => new Set([...prev, activeTactic]));
    setActiveTactic(null);
    setPhase('select');
  };

  const reviseTacticProposal = () => {
    setTacticProposals(prev => ({ ...prev, [activeTactic]: null }));
  };

  const backToSelect = () => {
    setActiveTactic(null);
    setPhase('select');
    setInputText('');
  };

  // ── Shared ─────────────────────────────────────────────────────────────────

  const executeProposals = async (proposals) => {
    await axios.post('/api/ai/execute', {
      proposals,
      agentId: currentUser?._id
    }, { headers: { 'x-auth-token': token } });
    await fetchCards();
  };

  const handleCardUpdate = (updatedCard) => {
    setCards(prev => prev.map(c => c._id === updatedCard._id ? updatedCard : c));
  };

  const handleCardExecuteProposal = async (proposals) => {
    try {
      await executeProposals(proposals);
    } catch (err) {
      console.error('Execute error:', err);
      alert('Failed to update card: ' + (err.response?.data?.msg || err.message));
    }
  };

  const activeTacticInfo = COPY_TYPES.find(t => t.id === activeTactic);

  // ── Phase: DISCOVERY ───────────────────────────────────────────────────────

  if (phase === 'discovery') {
    return (
      <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900">
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100">{board.name}</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Marketing Copy Studio</p>
        </div>

        <div className="flex flex-col flex-1 overflow-hidden max-w-2xl mx-auto w-full px-4 py-6">
          <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm flex flex-col flex-1 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center text-lg">🎯</div>
                <div>
                  <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">Brand Discovery</h2>
                  <p className="text-xs text-gray-400 dark:text-gray-500">Let's learn about your brand first.</p>
                </div>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6 space-y-4">
              {brandHistory.length === 0 && (
                <div className="text-center py-10">
                  <div className="text-4xl mb-4">✨</div>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Welcome to Marketing Copy Studio</p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 max-w-sm mx-auto">
                    Before we start creating copy, I need to understand your brand. Start by describing your product or service.
                  </p>
                </div>
              )}

              {brandHistory.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] text-sm px-4 py-2.5 rounded-2xl leading-relaxed ${
                    msg.role === 'user'
                      ? 'bg-blue-600 text-white rounded-br-sm'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-bl-sm'
                  }`}>
                    {msg.content}
                  </div>
                </div>
              ))}

              {chatLoading && (
                <div className="flex justify-start">
                  <div className="bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-sm px-4 py-2.5 rounded-2xl rounded-bl-sm">
                    <span className="animate-pulse">Thinking...</span>
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>

            <div className="p-4 border-t border-gray-100 dark:border-gray-700">
              <div className="flex gap-2">
                <input
                  ref={inputRef}
                  type="text"
                  className="flex-1 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-xl px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-blue-300"
                  placeholder={brandHistory.length === 0 ? 'Describe your product or service...' : 'Continue...'}
                  value={inputText}
                  onChange={e => setInputText(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendBrandMessage()}
                  disabled={chatLoading}
                />
                <button
                  onClick={sendBrandMessage}
                  disabled={chatLoading || !inputText.trim()}
                  className="bg-blue-600 text-white px-5 py-2.5 rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm font-medium"
                >
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Right Panel (shared by select + tacticChat) ────────────────────────────

  const RightPanel = () => (
    <div className="flex-1 overflow-y-auto p-5">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
          All Cards
          {cards.length > 0 && <span className="ml-2 text-gray-400 dark:text-gray-500 font-normal">({cards.length})</span>}
        </h2>
      </div>

      {cardsLoading ? (
        <div className="text-center py-12 text-gray-400 dark:text-gray-500 text-sm">Loading cards...</div>
      ) : cards.length === 0 ? (
        <div className="text-center py-16">
          <div className="text-5xl mb-4 opacity-20">🗂️</div>
          <p className="text-sm text-gray-400 dark:text-gray-500 font-medium">No cards yet</p>
          <p className="text-xs text-gray-300 dark:text-gray-400 mt-1">
            Select a tactic and generate copy to see cards here.
          </p>
        </div>
      ) : (
        <div className="grid gap-3 grid-cols-1 xl:grid-cols-2">
          {cards.map(card => (
            <MarketingCard
              key={card._id}
              card={card}
              token={token}
              onUpdate={handleCardUpdate}
              onExecuteProposal={handleCardExecuteProposal}
            />
          ))}
        </div>
      )}
    </div>
  );

  // ── Phase: SELECT ──────────────────────────────────────────────────────────

  if (phase === 'select') {
    return (
      <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900">
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-3">
          <h1 className="text-lg font-bold text-gray-900 dark:text-gray-100">{board.name}</h1>
          <p className="text-xs text-gray-500 dark:text-gray-400">Marketing Copy Studio</p>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Left: Brand summary + tactic grid */}
          <div className="w-80 flex-shrink-0 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-y-auto">
            {/* Brand summary */}
            {brandContext && (
              <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm">✅</span>
                  <h3 className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">Your Brand</h3>
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed mb-3">{brandContext.summary}</p>
                <div className="space-y-1">
                  {brandContext.company && (
                    <div className="flex gap-1.5 text-xs">
                      <span className="text-gray-400 dark:text-gray-500 w-16 flex-shrink-0">Company</span>
                      <span className="text-gray-700 dark:text-gray-300 font-medium">{brandContext.company}</span>
                    </div>
                  )}
                  {brandContext.audience && (
                    <div className="flex gap-1.5 text-xs">
                      <span className="text-gray-400 dark:text-gray-500 w-16 flex-shrink-0">Audience</span>
                      <span className="text-gray-700 dark:text-gray-300">{brandContext.audience}</span>
                    </div>
                  )}
                  {brandContext.tone && (
                    <div className="flex gap-1.5 text-xs">
                      <span className="text-gray-400 dark:text-gray-500 w-16 flex-shrink-0">Tone</span>
                      <span className="text-gray-700 dark:text-gray-300">{brandContext.tone}</span>
                    </div>
                  )}
                  {brandContext.usp && (
                    <div className="flex gap-1.5 text-xs">
                      <span className="text-gray-400 dark:text-gray-500 w-16 flex-shrink-0">USP</span>
                      <span className="text-gray-700 dark:text-gray-300">{brandContext.usp}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Tactic selector */}
            <div className="p-4">
              <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3">What to create?</h3>
              <div className="space-y-1.5">
                {COPY_TYPES.map(ct => {
                  const isDone = generatedTactics.has(ct.id);
                  const cardCount = cards.filter(c => c.marketingCopy?.copyType === ct.id).length;

                  return (
                    <div key={ct.id}>
                      <button
                        onClick={() => handleTacticClick(ct.id)}
                        disabled={chatLoading}
                        className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                          isDone
                            ? 'bg-green-50 dark:bg-green-900 text-green-700 dark:text-green-100 hover:bg-green-100 dark:hover:bg-green-800 border border-green-200 dark:border-green-700'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 border border-transparent hover:border-gray-200 dark:hover:border-gray-600'
                        } disabled:opacity-50`}
                      >
                        <span className="flex items-center gap-2">
                          <span>{ct.icon}</span>
                          <span>{ct.label}</span>
                          {ct.id === 'social' && <span className="text-gray-400 dark:text-gray-500 text-xs">▾</span>}
                        </span>
                        <span className="flex items-center gap-1.5">
                          {isDone && <span className="text-green-500 text-xs">✓</span>}
                          {cardCount > 0 && (
                            <span className={`text-xs rounded-full px-1.5 py-0.5 font-bold ${
                              isDone ? 'bg-green-200 dark:bg-green-800 text-green-700 dark:text-green-100' : 'bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                            }`}>
                              {cardCount}
                            </span>
                          )}
                        </span>
                      </button>

                      {/* Social platform expansion */}
                      {ct.id === 'social' && socialExpanded && (
                        <div className="ml-8 mt-2 mb-1 space-y-2">
                          <div className="flex flex-wrap gap-1.5">
                            {SOCIAL_PLATFORMS.map(p => (
                              <button
                                key={p}
                                onClick={() => setSelectedPlatforms(prev =>
                                  prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]
                                )}
                                className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                                  selectedPlatforms.includes(p)
                                    ? 'bg-blue-600 text-white border-blue-600'
                                    : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-blue-300 dark:hover:border-blue-400'
                                }`}
                              >
                                {p}
                              </button>
                            ))}
                          </div>
                          <button
                            onClick={handleSocialGenerate}
                            disabled={selectedPlatforms.length === 0 || chatLoading}
                            className="w-full text-xs bg-blue-600 text-white py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
                          >
                            {generatedTactics.has('social') ? 'Regenerate Social' : 'Generate Social Copy'}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {chatLoading && (
                <div className="mt-3 text-center text-xs text-gray-400 dark:text-gray-500 animate-pulse">Generating...</div>
              )}
            </div>
          </div>

          {/* Right: All cards */}
          <RightPanel />
        </div>
      </div>
    );
  }

  // ── Phase: TACTIC CHAT ─────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full bg-gray-50 dark:bg-gray-900">
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={backToSelect}
            className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
          >
            <span>←</span>
            <span>Back</span>
          </button>
          <span className="text-gray-300 dark:text-gray-600">|</span>
          <div className="flex items-center gap-1.5">
            <span>{activeTacticInfo?.icon}</span>
            <span className="text-sm font-semibold text-gray-800 dark:text-gray-100">{activeTacticInfo?.label}</span>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Left: Tactic chat */}
        <div className="w-2/5 flex flex-col border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300">{activeTacticInfo?.label} Details</h2>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {currentTacticHistory.length === 0
                ? 'A few quick questions about this specific tactic.'
                : 'Continue the conversation...'}
            </p>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {currentTacticHistory.length === 0 && !chatLoading && (
              <div className="text-center py-8">
                <div className="text-3xl mb-3">{activeTacticInfo?.icon}</div>
                <p className="text-sm text-gray-500 dark:text-gray-400 font-medium">
                  Ready to create {activeTacticInfo?.label} copy
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                  I'll ask a few tactic-specific questions before generating.
                </p>
              </div>
            )}

            {currentTacticHistory.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] text-sm px-3 py-2 rounded-xl leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-blue-600 text-white rounded-br-sm'
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 rounded-bl-sm'
                }`}>
                  {msg.content}
                </div>
              </div>
            ))}

            {chatLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 text-sm px-3 py-2 rounded-xl rounded-bl-sm">
                  <span className="animate-pulse">Thinking...</span>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Proposal banner */}
          {currentTacticProposal && (
            <div className="mx-3 mb-2 bg-green-50 dark:bg-green-900 border border-green-300 dark:border-green-700 rounded-xl p-3">
              <p className="text-sm font-semibold text-green-800 dark:text-green-100 mb-1">
                Ready to create {currentTacticProposal.proposals?.length} card{currentTacticProposal.proposals?.length !== 1 ? 's' : ''}
              </p>
              <p className="text-xs text-green-600 dark:text-green-300 mb-2">{currentTacticProposal.text}</p>
              <div className="flex gap-2">
                <button
                  onClick={approveTacticProposal}
                  className="flex-1 text-sm bg-green-600 text-white py-1.5 rounded-lg hover:bg-green-700 font-medium"
                >
                  Approve & Create
                </button>
                <button
                  onClick={reviseTacticProposal}
                  className="text-sm text-green-700 dark:text-green-300 border border-green-300 dark:border-green-700 px-3 py-1.5 rounded-lg hover:bg-green-100 dark:hover:bg-green-800"
                >
                  Revise
                </button>
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-3 border-t border-gray-100 dark:border-gray-700">
            <div className="flex gap-2">
              <input
                ref={inputRef}
                type="text"
                className="flex-1 text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-300"
                placeholder={currentTacticHistory.length === 0 ? 'Start chatting or send anything to begin...' : 'Continue the conversation...'}
                value={inputText}
                onChange={e => setInputText(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && !e.shiftKey && sendTacticMessage()}
                disabled={chatLoading}
              />
              <button
                onClick={sendTacticMessage}
                disabled={chatLoading || !inputText.trim()}
                className="bg-blue-600 text-white px-4 py-2 rounded-xl hover:bg-blue-700 disabled:opacity-50 transition-colors text-sm font-medium"
              >
                Send
              </button>
            </div>
          </div>
        </div>

        {/* Right: All cards */}
        <RightPanel />
      </div>
    </div>
  );
};

export default MarketingView;
